import { useState, useEffect } from "react";
import { Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AVATAR_URL = "/edgar-avatar-small.jpg";

export default function ResetPasswordPage() {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) { setInvalid(true); setLoading(false); return; }
    setToken(t);
    axios.get(`${API}/auth/reset-verify/${t}`)
      .then(res => { setEmail(res.data.email); setLoading(false); })
      .catch(() => { setInvalid(true); setLoading(false); });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || password.length < 6) {
      setError("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al cambiar contrasena");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 size={28} className="text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-zinc-100 mb-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Link invalido o expirado
          </h2>
          <p className="text-xs text-zinc-500 mb-6">Contacta al administrador para obtener un nuevo link.</p>
          <a href="/" className="text-emerald-400 text-sm hover:text-emerald-300">Ir al inicio</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-sm">
          <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-zinc-100 mb-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Contrasena actualizada
          </h2>
          <p className="text-xs text-zinc-500 mb-6">Ya puedes iniciar sesion con tu nueva contrasena.</p>
          <a href="/" className="inline-flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-emerald-400">
            Iniciar sesion
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full overflow-hidden ring-4 ring-emerald-500/20 mb-4">
            <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-xl font-bold text-zinc-50" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Nueva contrasena
          </h1>
          <p className="text-xs text-zinc-500 mt-1">{email}</p>
        </div>

        <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6">
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5 mb-4">
              <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1 block">
                Nueva contrasena
              </label>
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 6 caracteres"
                  required
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 pr-10 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-500 text-zinc-950 font-semibold rounded-full py-2.5 text-sm transition-all hover:bg-emerald-400 disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.2)] mt-2"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Cambiar contrasena"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
